import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import ReusableSearch from './ReusableSearch';
import { useSearch } from './useSearch';
import { toUrl } from './toURL';
import type { SearchRecord } from './types';
import { usePepwiseAuthController } from 'Context/contexts/AuthContext';
import useDataEstateSideDrawer from 'components/DataEstate/SideDrawers/useDrawerComponent';
import { useFinOpsDrawer } from 'Context/contexts/FinOpsDrawerContext';
import { useSearchContext } from 'Context/contexts/searchContext';
import { hideML } from '../../service/config';

export default function GlobalSearchContainer() {
  const navigate = useNavigate();
  const location = useLocation();

  const [controller] = usePepwiseAuthController();
  const capabilities = controller?.capabilities ?? [];

  const { query, setQuery, results, setResults, handleSearch } = useSearch();
  const { handleSetComponent } = useDataEstateSideDrawer();
  const { openDrawer } = useFinOpsDrawer();
  const { setSearchTerm, setSelectedTerm } = useSearchContext();

  const [isOpen, setIsOpen] = useState(false);
  const [selectedPath, setSelectedPath] = useState('');

  const platformObsCapabilities = [
    'EDW Jobs',
    'Tableau',
    'Data Growth',
    'Databricks',
    'Presto',
    'Power BI',
    'Data Management Framework',
    'Controls & Compliance',
    'Verification & Validation',
  ];

  const isPlatformObsVisible = platformObsCapabilities.some((cap) => capabilities.includes(cap));

  const filteredResults = useMemo(() => {
    return results.filter((result: SearchRecord) => {
      const module = result.module;

      if (module === 'D&AI Executive') {
        return capabilities.includes('Data Estate');
      }

      if (module === 'D&AI Monthly XOps') {
        return (
          capabilities.includes('Fin Ops') ||
          capabilities.includes('Data Estate') ||
          isPlatformObsVisible
        );
      }

      if (module === 'Data Pipeline') {
        return capabilities.includes('Data Ops');
      }

      if (module === 'Program Executive') {
        return capabilities.includes('IBP');
      }

      if (module === 'Finops') {
        return capabilities.includes('Fin Ops');
      }

      if (module === 'FinOps Executive') {
        return capabilities.includes('FinOps Executive');
      }

      if (module === 'Platform Observability') {
        return isPlatformObsVisible;
      }

      if (module === 'ML Observability' && !hideML) {
        return capabilities.includes('Machine Learning');
      }

      if (module === 'Data KPI and Alerts') {
        return capabilities.includes('Data KPI and Alerts');
      }

      if (module === 'Product Catalog') {
        return capabilities.includes('Product Catalog');
      }

      return false;
    });
  }, [results, capabilities, isPlatformObsVisible]);

  const handleChange = (value: string) => {
    setSearchTerm(value);
    setQuery(value);

    if (value.length >= 3) {
      handleSearch(value);
      setIsOpen(true);
    } else {
      setResults([]);
      setIsOpen(false);
    }
  };

  const handleSelect = (result: SearchRecord) => {
    setSelectedTerm(result.label);

    const url = toUrl(result);
    const [pathname, hashPart] = url.split('#');
    setSelectedPath(pathname);

    const hash = hashPart ? decodeURIComponent(hashPart) : '';

    const scrollToHash = () => {
      if (!hash) return;

      let tries = 0;

      const tryScroll = () => {
        const el = document.getElementById(hash);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } else if (tries++ < 20) {
          setTimeout(tryScroll, 100);
        }
      };

      tryScroll();
    };

    if (result.drawerType === 'FINOPS_COST_BREAKUP' && result.drawerProps) {
      navigate(pathname);
      setTimeout(() => {
        openDrawer(result.drawerProps!.title);
      }, 300);
      setIsOpen(false);
      return;
    }

    if (result.drawerType && result.drawerProps) {
      navigate(pathname);

      setTimeout(() => {
        handleSetComponent(result.drawerType!, result.drawerProps!);
        scrollToHash();
      }, 400);

      setIsOpen(false);
      return;
    }

    if (!hash) {
      navigate(pathname);
      setIsOpen(false);
      return;
    }

    if (location.pathname === pathname) {
      history.pushState(null, '', `#${hash}`);
      scrollToHash();
      setIsOpen(false);
    } else {
      navigate(`${pathname}#${hash}`);
      setTimeout(scrollToHash, 200);
      setIsOpen(false);
    }
  };

  useEffect(() => {
    if (selectedPath && location.pathname !== selectedPath) {
      setSearchTerm('');
      setQuery('');
      setSelectedTerm('');
      setSelectedPath('');
    }
  }, [location.pathname, selectedPath, setQuery, setSearchTerm, setSelectedTerm]);

  return (
    <ReusableSearch
      id="header-search-bar"
      value={query}
      results={filteredResults}
      onChange={handleChange}
      onSelect={handleSelect}
      isOpen={isOpen}
      onOpenChange={setIsOpen}
      placeholder="Search"
      minSearchChars={3}
      disabled={location.pathname === '/dashboard/sessionTimeOut'}
      width={{ xs: 160, sm: 220, md: 260, lg: 300 }}
    />
  );
}
